import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import type { DragEvent } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { BoardSkeleton } from './BoardSkeleton';
import {
  Sparkles,
  CircleDashed,
  ShieldAlert,
  Paperclip,
  Zap,
  Plus,
  MoreHorizontal,
  LayoutGrid,
  Folder,
  Hash,
  ArrowUpDown,
  GripVertical,
  GitBranch,
  Search,
  X,
} from 'lucide-react';
import type {
  Task,
  Priority,
  GroupingState,
  SortingState,
} from '@/types';
import { isOverdue } from '@/utils/formatters';
import { TASK_STATUS, normalizeStatus } from '@/constants/taskStatuses';
import {
  ViewToolbar,
  ShowCompletedToggle,
  MultiGroupingSelector,
  MultiSortingSelector,
  MultiFilterSelector,
  type BreadcrumbItem,
} from '@/components/shared/view-toolbar';
import {
  StatusSelect,
  PrioritySelect,
  InlineDatePicker,
  AssigneesEditor,
  TagsEditor,
  EditableText,
} from '@/components/shared/inline-editors';
import { CustomFieldsInline } from '@/components/fields/CustomFieldsInline';
import clsx from 'clsx';
import {
  getTaskGroupingOptions,
  createDefaultGroupingState,
  getTaskSortingOptions,
  createDefaultSortingState,
  sortItems,
} from '@/utils/grouping';
import { getAllFilterOptions } from '@/utils/filterOptions';
import { useProfilesContext } from '@/context/ProfilesContext';
import { Filter } from 'lucide-react';
import { useViewData } from '@/hooks/useViewData';
import { useTaskActions } from '@/hooks/useTaskActions';
import { TaskContextMenu } from '@/components/task/TaskContextMenu';
import { getFileUrl } from '@/db/storage';

const COVER_IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

function CoverImage({ path }: { path: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getFileUrl(path).then(u => { if (!cancelled) setUrl(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [path]);
  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      className="w-full h-28 object-cover rounded-t-lg -m-3 mb-2"
      style={{ width: 'calc(100% + 24px)' }}
      loading="lazy"
    />
  );
}
import { useDialogStore } from '@/store/dialogStore';
import { useAppStore } from '@/store/appStore';
import { bulkDuplicateTasks } from '@/db/index';
import { useClickOutside } from '@/hooks/useClickOutside';

interface Column {
  id: string;
  status: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  bgClass: string;
  borderClass: string;
  dotColor: string;
}

const columns: Column[] = [
  {
    id: TASK_STATUS.OPEN,
    status: TASK_STATUS.OPEN,
    title: 'Da fare',
    icon: <CircleDashed className="w-3.5 h-3.5" />,
    color: 'text-text-tertiary',
    bgClass: 'bg-surface-tertiary/30',
    borderClass: 'border-border-primary',
    dotColor: 'bg-text-quaternary',
  },
  {
    id: TASK_STATUS.IN_PROGRESS,
    status: TASK_STATUS.IN_PROGRESS,
    title: 'In corso',
    icon: <Zap className="w-3.5 h-3.5" />,
    color: 'text-accent-blue',
    bgClass: 'bg-accent-blue/5',
    borderClass: 'border-accent-blue/20',
    dotColor: 'bg-accent-blue',
  },
  {
    id: TASK_STATUS.BLOCKED,
    status: TASK_STATUS.BLOCKED,
    title: 'Bloccato',
    icon: <ShieldAlert className="w-3.5 h-3.5" />,
    color: 'text-accent-rose',
    bgClass: 'bg-accent-rose/5',
    borderClass: 'border-accent-rose/20',
    dotColor: 'bg-accent-rose',
  },
  {
    id: TASK_STATUS.COMPLETED,
    status: TASK_STATUS.COMPLETED,
    title: 'Completato',
    icon: <Sparkles className="w-3.5 h-3.5" />,
    color: 'text-accent-emerald',
    bgClass: 'bg-accent-emerald/5',
    borderClass: 'border-accent-emerald/20',
    dotColor: 'bg-accent-emerald',
  },
];

export function BoardView() {
  const {
    tasks,
    enrichedTasks,
    filters,
    grouping,
    sorting,
    viewMode,
    availableAssignees,
    availableTags,
    currentPath,
    loading,
    onTaskClick,
    onFilterChange,
    onGroupingChange,
    onSortingChange,
    onNavigate,
    onViewModeChange,
    containersMap,
  } = useViewData();
  // Gli URL usano gli id degli spazi: senza risolverli il titolo e le briciole
  // di pane mostrano un UUID al posto del nome (come già fa TaskList).
  const getNameForId = useCallback((id: string): string => {
    return containersMap?.get(id)?.name ?? id;
  }, [containersMap]);

  // Build subtask count map from all tasks (unfiltered)
  const subtasksCountMap = useMemo(() => {
    const map = new Map<string, number>();
    enrichedTasks.forEach((t) => {
      if (t.parentId) {
        map.set(t.parentId, (map.get(t.parentId) || 0) + 1);
      }
    });
    return map;
  }, [enrichedTasks]);

  const { handleUpdateTask, handleDeleteTask } = useTaskActions();
  const dialogConfirm = useDialogStore((s) => s.confirm);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [swimlaneBy, setSwimlaneBy] = useState<'none' | 'priority' | 'assignee'>('none');
  const [wipLimits, setWipLimits] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem('zenda-board-wip-limits');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const updateWipLimit = useCallback((columnId: string, limit: number | null) => {
    setWipLimits(prev => {
      const next = { ...prev };
      if (limit === null || limit <= 0) delete next[columnId];
      else next[columnId] = limit;
      localStorage.setItem('zenda-board-wip-limits', JSON.stringify(next));
      return next;
    });
  }, []);

  // Task context menu state
  const [taskContextMenu, setTaskContextMenu] = useState<{
    isOpen: boolean;
    x: number;
    y: number;
    task: Task | null;
  }>({ isOpen: false, x: 0, y: 0, task: null });
  const taskContextMenuRef = useRef<HTMLDivElement>(null);

  const closeTaskContextMenu = useCallback(() => {
    setTaskContextMenu(prev => ({ ...prev, isOpen: false }));
  }, []);

  useClickOutside([taskContextMenuRef], closeTaskContextMenu, taskContextMenu.isOpen);

  const handleTaskContextMenu = useCallback((e: React.MouseEvent, task: Task) => {
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 300);
    setTaskContextMenu({ isOpen: true, x, y, task });
  }, []);
  const [localGrouping, setLocalGrouping] = useState<GroupingState>(
    () => grouping || createDefaultGroupingState()
  );
  const [localSorting, setLocalSorting] = useState<SortingState>(
    () => sorting || createDefaultSortingState()
  );

  // Grouping handlers
  const handleGroupingChange = useCallback(
    (newGrouping: GroupingState) => {
      setLocalGrouping(newGrouping);
      onGroupingChange(newGrouping);
    },
    [onGroupingChange]
  );

  // Sorting handlers
  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setLocalSorting(newSorting);
      onSortingChange(newSorting);
    },
    [onSortingChange]
  );

  // Unique values for filters
  const uniqueValues = useMemo(() => {
    return {
      statuses: Array.from(new Set(tasks.map((t) => normalizeStatus(t.status)))).filter(Boolean),
      assignees: Array.from(new Set(tasks.flatMap((t) => (t.assignees || []).filter(Boolean)))).filter((a): a is string => a !== null),
      tags: Array.from(new Set(tasks.flatMap((t) => (t.tags || []).filter(Boolean)))).filter((t): t is string => t !== null),
      priorities: Array.from(new Set(tasks.map((t) => t.priority).filter(Boolean))),
    };
  }, [tasks]);

  // Filter options with consistent ordering
  const { resolveDisplayName } = useProfilesContext();
  const filterOptions = useMemo(() => {
    return getAllFilterOptions(
      uniqueValues.statuses,
      uniqueValues.assignees,
      uniqueValues.tags,
      uniqueValues.priorities as Priority[],
      resolveDisplayName,
    );
  }, [uniqueValues, resolveDisplayName]);

  // Compute breadcrumbs (exclude current page)
  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [];

    if (!currentPath) {
      return items;
    }

    const pathParts = currentPath.split('/');

    // If we're on a list (3 parts), show space and folder breadcrumbs
    if (pathParts.length === 3) {
      // Space breadcrumb
      items.push({
        label: getNameForId(pathParts[0]),
        onClick: onNavigate ? () => onNavigate(pathParts[0]) : undefined,
      });
      // Folder breadcrumb
      items.push({
        label: getNameForId(pathParts[1]),
        onClick: onNavigate ? () => onNavigate(`${pathParts[0]}/${pathParts[1]}`) : undefined,
      });
    } else if (pathParts.length === 2) {
      // If we're on a folder or list without folder, show only space breadcrumb
      items.push({
        label: getNameForId(pathParts[0]),
        onClick: onNavigate ? () => onNavigate(pathParts[0]) : undefined,
      });
    }
    // If we're on a space (1 part) or root, no breadcrumbs needed

    return items;
  }, [currentPath, onNavigate, getNameForId]);

  // Compute toolbar title and icon based on navigation context
  const { toolbarIcon, toolbarTitle, toolbarSubtitle } = useMemo(() => {
    const taskCount = `${tasks.length} task`;
    if (!currentPath) {
      return {
        toolbarIcon: <LayoutGrid className="w-4 h-4 text-accent-violet" />,
        toolbarTitle: 'Tutti i task',
        toolbarSubtitle: taskCount,
      };
    }

    const pathParts = currentPath.split('/');
    const lastPart = getNameForId(pathParts[pathParts.length - 1]);

    // Determine type based on path length and context
    if (pathParts.length === 3) {
      // Likely a list (space/folder/list)
      return {
        toolbarIcon: <Hash className="w-4 h-4 text-accent-emerald" />,
        toolbarTitle: lastPart,
        toolbarSubtitle: taskCount,
      };
    } else if (pathParts.length === 2) {
      // Could be folder or list without folder
      return {
        toolbarIcon: <Folder className="w-4 h-4 text-accent-blue" />,
        toolbarTitle: lastPart,
        toolbarSubtitle: taskCount,
      };
    } else {
      // Single part - space
      return {
        toolbarIcon: <Zap className="w-4 h-4 text-accent-amber" />,
        toolbarTitle: lastPart,
        toolbarSubtitle: taskCount,
      };
    }
  }, [currentPath, tasks.length, getNameForId]);

  // Sort tasks before distributing into columns
  const sortedTasks = useMemo(() => {
    const statusOrder: Record<string, number> = {
      [TASK_STATUS.IN_PROGRESS]: 1,
      [TASK_STATUS.OPEN]: 2,
      [TASK_STATUS.TODO]: 3,
      [TASK_STATUS.BLOCKED]: 4,
      [TASK_STATUS.COMPLETED]: 5,
      [TASK_STATUS.CANCELLED]: 6,
    };
    const priorityOrder: Record<string, number> = { urgent: 1, high: 2, normal: 3, low: 4 };

    return sortItems(tasks, localSorting, (a, b, field, order) => {
      let comparison = 0;

      switch (field) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'status': {
          const sA = statusOrder[normalizeStatus(a.status)] ?? 99;
          const sB = statusOrder[normalizeStatus(b.status)] ?? 99;
          comparison = sA - sB;
          break;
        }
        case 'dueDate': {
          const hasA = Boolean(a.dueDate);
          const hasB = Boolean(b.dueDate);
          if (!hasA && !hasB) comparison = 0;
          else if (!hasA) comparison = 1;
          else if (!hasB) comparison = -1;
          else comparison = (a.dueDate as number) - (b.dueDate as number);
          break;
        }
        case 'dateCreated':
          comparison = (a.dateCreated || 0) - (b.dateCreated || 0);
          break;
        case 'priority': {
          const pA = priorityOrder[a.priority as string] ?? 5;
          const pB = priorityOrder[b.priority as string] ?? 5;
          comparison = pA - pB;
          break;
        }
        case 'assignee': {
          const aA = (a.assignees || [])[0] || '';
          const aB = (b.assignees || [])[0] || '';
          comparison = aA.localeCompare(aB);
          break;
        }
        case 'updated': {
          const uA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const uB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          comparison = uA - uB;
          break;
        }
        default:
          comparison = 0;
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [tasks, localSorting]);

  const tasksByStatus = useMemo(() => {
    const grouped = new Map<string, Task[]>();

    columns.forEach((col) => grouped.set(col.id, []));

    sortedTasks.forEach((task) => {
      const status = normalizeStatus(task.status);
      let columnId: string = TASK_STATUS.OPEN;

      if (status === TASK_STATUS.COMPLETED || status === TASK_STATUS.CANCELLED) {
        columnId = TASK_STATUS.COMPLETED;
      } else if (status === TASK_STATUS.IN_PROGRESS) {
        columnId = TASK_STATUS.IN_PROGRESS;
      } else if (status === TASK_STATUS.BLOCKED) {
        columnId = TASK_STATUS.BLOCKED;
      } else {
        columnId = TASK_STATUS.OPEN;
      }

      const existing = grouped.get(columnId) || [];
      existing.push(task);
      grouped.set(columnId, existing);
    });

    return grouped;
  }, [sortedTasks]);

  // Swimlane groups
  const swimlanes = useMemo(() => {
    if (swimlaneBy === 'none') return null;
    const groups = new Map<string, Task[]>();
    sortedTasks.forEach(task => {
      let keys: string[];
      if (swimlaneBy === 'priority') {
        keys = [task.priority || 'normal'];
      } else {
        keys = (task.assignees?.length) ? task.assignees : ['non_assegnato'];
      }
      keys.forEach(key => {
        const arr = groups.get(key) || [];
        arr.push(task);
        groups.set(key, arr);
      });
    });
    // Sort swimlane keys
    const order = swimlaneBy === 'priority'
      ? ['urgent', 'high', 'normal', 'low']
      : [...groups.keys()].sort();
    return order.filter(k => groups.has(k)).map(key => ({
      key,
      label: swimlaneBy === 'priority'
        ? { urgent: 'Urgente', high: 'Alta', normal: 'Normale', low: 'Bassa' }[key] || key
        : key === 'non_assegnato' ? 'Non assegnato' : key,
      tasks: groups.get(key) || [],
    }));
  }, [sortedTasks, swimlaneBy]);

  const handleDragStart = (e: DragEvent, taskId: string) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  };

  const handleDragEnd = () => {
    // Fallback for cancelled drags (Escape, drop outside valid target).
    // For successful drops, handleDrop already handles cleanup.
    setDraggedTaskId(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: DragEvent, column: Column) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');

    if (taskId && handleUpdateTask) {
      handleUpdateTask(taskId, { status: column.status });
    }

    // Clear column highlight immediately. Delay clearing draggedTaskId so
    // React processes the optimistic update first (card moves to new column),
    // then the card becomes visible there — avoiding a flash at the old position.
    // Note: handleDragEnd won't fire because React unmounts the original element
    // when the optimistic update moves the card, so we MUST clear here.
    setDragOverColumn(null);
    requestAnimationFrame(() => {
      setDraggedTaskId(null);
    });
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden animate-fade-in">
      {/* Toolbar */}
      <ViewToolbar
        icon={toolbarIcon}
        title={toolbarTitle}
        subtitle={toolbarSubtitle}
        breadcrumbs={breadcrumbs}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        showViewSwitcher={!!onViewModeChange}
        onOpenSidebar={() => useAppStore.getState().setMobileSidebarOpen(true)}
        leftContent={
          filters &&
          onFilterChange && (
            <div className="flex items-center gap-2 gap-y-2 flex-wrap">
              <MultiFilterSelector
                filters={{
                  status: filters.status || [],
                  assignees: filters.assignees || [],
                  tags: filters.tags || [],
                  priority: filters.priority || [],
                }}
                onFilterChange={onFilterChange}
                availableOptions={filterOptions}
                icon={<Filter className="w-3.5 h-3.5 text-text-tertiary" />}
              />
              <MultiGroupingSelector
                grouping={localGrouping}
                onGroupingChange={handleGroupingChange}
                availableOptions={getTaskGroupingOptions()}
                icon={<LayoutGrid className="w-3.5 h-3.5 text-text-tertiary" />}
              />
              <MultiSortingSelector
                sorting={localSorting}
                onSortingChange={handleSortingChange}
                availableOptions={getTaskSortingOptions()}
                icon={<ArrowUpDown className="w-3.5 h-3.5 text-text-tertiary" />}
              />
              <ShowCompletedToggle
                showCompleted={filters.showCompleted}
                onToggle={(show) => onFilterChange({ showCompleted: show })}
              />
              <div className="relative">
                <button
                  onClick={() => {
                    const opts: Array<'none' | 'priority' | 'assignee'> = ['none', 'priority', 'assignee'];
                    const idx = opts.indexOf(swimlaneBy);
                    setSwimlaneBy(opts[(idx + 1) % opts.length]);
                  }}
                  className={clsx(
                    'flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] transition-colors',
                    swimlaneBy !== 'none'
                      ? 'bg-accent-violet/10 text-accent-violet'
                      : 'text-text-tertiary hover:bg-surface-hover'
                  )}
                  title="Swimlanes"
                >
                  <GitBranch className="w-3.5 h-3.5" />
                  {swimlaneBy === 'none' ? 'Swimlane' : swimlaneBy === 'priority' ? 'Priorità' : 'Assegnatario'}
                </button>
              </div>
            </div>
          )
        }
      />

      {/* Board Content */}
      <div className="flex-1 overflow-auto p-4">
        {loading && tasks.length === 0 ? (
          <BoardSkeleton />
        ) : swimlanes ? (
          /* Swimlane layout */
          <div className="space-y-4 min-w-max">
            {swimlanes.map((lane) => {
              const laneTaskIds = new Set(lane.tasks.map(t => t.id));
              return (
                <div key={lane.key}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">
                      {swimlaneBy === 'assignee' ? resolveDisplayName(lane.key) : lane.label}
                    </span>
                    <span className="text-[10px] text-text-quaternary tabular-nums">{lane.tasks.length}</span>
                    <div className="flex-1 h-px bg-border-primary/40" />
                  </div>
                  <div className="flex gap-4">
                    {columns.map((column, columnIndex) => {
                      const allColTasks = tasksByStatus.get(column.id) || [];
                      const columnTasks = allColTasks.filter(t => laneTaskIds.has(t.id));
                      const colKey = `${lane.key}-${column.id}`;
                      return (
                        <BoardColumn
                          key={colKey}
                          column={column}
                          tasks={columnTasks}
                          onTaskClick={onTaskClick}
                          onUpdateTask={handleUpdateTask}
                          availableAssignees={availableAssignees}
                          availableTags={availableTags}
                          delay={columnIndex * 30}
                          draggedTaskId={draggedTaskId}
                          isDragOver={dragOverColumn === column.id}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, column.id)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, column)}
                          onContextMenu={handleTaskContextMenu}
                          subtasksCountMap={subtasksCountMap}
                          filterText={columnFilters[colKey] || ''}
                          onFilterChange={(text) => setColumnFilters(prev => ({ ...prev, [colKey]: text }))}
                          wipLimit={wipLimits[column.id] ?? null}
                          onWipLimitChange={(limit) => updateWipLimit(column.id, limit)}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Standard flat layout */
          <div className="flex gap-4 min-w-max h-full">
            {columns.map((column, columnIndex) => {
              const columnTasks = tasksByStatus.get(column.id) || [];
              return (
                <BoardColumn
                  key={column.id}
                  column={column}
                  tasks={columnTasks}
                  onTaskClick={onTaskClick}
                  onUpdateTask={handleUpdateTask}
                  availableAssignees={availableAssignees}
                  availableTags={availableTags}
                  delay={columnIndex * 50}
                  draggedTaskId={draggedTaskId}
                  isDragOver={dragOverColumn === column.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, column.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, column)}
                  onContextMenu={handleTaskContextMenu}
                  subtasksCountMap={subtasksCountMap}
                  filterText={columnFilters[column.id] || ''}
                  onFilterChange={(text) => setColumnFilters(prev => ({ ...prev, [column.id]: text }))}
                  wipLimit={wipLimits[column.id] ?? null}
                  onWipLimitChange={(limit) => updateWipLimit(column.id, limit)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Task Context Menu */}
      {taskContextMenu.isOpen && taskContextMenu.task && (
        <TaskContextMenu
          ref={taskContextMenuRef}
          x={taskContextMenu.x}
          y={taskContextMenu.y}
          task={taskContextMenu.task}
          onStatusChange={(status) => {
            handleUpdateTask(taskContextMenu.task!.id, { status });
          }}
          onPriorityChange={(priority) => {
            handleUpdateTask(taskContextMenu.task!.id, { priority });
          }}
          onDuplicate={async () => {
            try {
              await bulkDuplicateTasks([taskContextMenu.task!.id]);
            } catch {
              // Error handled by the function
            }
          }}
          onDelete={async () => {
            const confirmed = await dialogConfirm({
              title: 'Elimina task',
              message: `Sei sicuro di voler eliminare "${taskContextMenu.task!.name}"?`,
              confirmLabel: 'Elimina',
              variant: 'danger',
            });
            if (confirmed) handleDeleteTask(taskContextMenu.task!.id);
          }}
          onClose={closeTaskContextMenu}
        />
      )}
    </div>
  );
}

interface BoardColumnProps {
  column: Column;
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
  onUpdateTask?: (taskId: string, updates: Partial<Task>) => void;
  availableAssignees?: string[];
  availableTags?: string[];
  delay: number;
  draggedTaskId: string | null;
  isDragOver: boolean;
  onDragStart: (e: DragEvent, taskId: string) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent) => void;
  onContextMenu?: (e: React.MouseEvent, task: Task) => void;
  subtasksCountMap: Map<string, number>;
  filterText: string;
  onFilterChange: (text: string) => void;
  wipLimit: number | null;
  onWipLimitChange: (limit: number | null) => void;
}

function BoardColumn({
  column,
  tasks,
  onTaskClick,
  onUpdateTask,
  availableAssignees = [],
  availableTags = [],
  delay,
  draggedTaskId,
  isDragOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onContextMenu,
  subtasksCountMap,
  filterText,
  onFilterChange,
  wipLimit,
  onWipLimitChange,
}: BoardColumnProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const filterInputRef = useRef<HTMLInputElement>(null);
  const [showFilter, setShowFilter] = useState(false);
  const [showWipMenu, setShowWipMenu] = useState(false);
  const [wipDraft, setWipDraft] = useState('');
  const wipMenuRef = useRef<HTMLDivElement>(null);
  const isOverWip = wipLimit !== null && tasks.length > wipLimit;

  const filteredTasks = useMemo(() => {
    if (!filterText.trim()) return tasks;
    const q = filterText.toLowerCase();
    return tasks.filter(t =>
      t.name.toLowerCase().includes(q) ||
      (t.tags || []).some(tag => tag.toLowerCase().includes(q)) ||
      (t.assignees || []).some(a => a.toLowerCase().includes(q))
    );
  }, [tasks, filterText]);

  useEffect(() => {
    if (showFilter && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [showFilter]);

  const rowVirtualizer = useVirtualizer({
    count: filteredTasks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 140,
    overscan: 5,
    measureElement: (element) => {
      return element.getBoundingClientRect().height;
    },
  });

  return (
    <div
      className={clsx(
        'w-72 flex flex-col bg-surface-secondary/30 rounded-xl border animate-slide-in-up overflow-hidden transition-all',
        isDragOver
          ? `${column.borderClass} ring-2 ring-accent-violet/30 bg-accent-violet/5`
          : isOverWip
            ? 'border-amber-500/50 ring-1 ring-amber-500/20'
            : 'border-border-primary'
      )}
      style={{ animationDelay: `${delay}ms` }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Column Header */}
      <div className={clsx('px-3 py-2.5', column.bgClass)}>
        <div className="flex items-center gap-2.5">
          <div className={clsx('w-2 h-2 rounded-full', column.dotColor)} />
          <h3 className={clsx('text-[11px] font-semibold uppercase tracking-wide', column.color)}>
            {column.title}
          </h3>
          <span
            className={clsx(
              'ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold tabular-nums',
              isOverWip ? 'bg-amber-500/15 text-amber-600' : [column.bgClass, column.color]
            )}
          >
            {filterText
              ? `${filteredTasks.length}/${tasks.length}`
              : wipLimit !== null
                ? `${tasks.length}/${wipLimit}`
                : tasks.length}
          </span>
          <button
            onClick={() => {
              if (showFilter) {
                onFilterChange('');
                setShowFilter(false);
              } else {
                setShowFilter(true);
              }
            }}
            className={clsx(
              'p-1 rounded transition-colors',
              showFilter || filterText
                ? 'bg-accent-violet/10 text-accent-violet'
                : 'hover:bg-surface-hover text-text-quaternary'
            )}
            title="Filtra colonna"
          >
            <Search className="w-3.5 h-3.5" />
          </button>
          <div className="relative">
            <button
              onClick={() => { setShowWipMenu(!showWipMenu); setWipDraft(wipLimit !== null ? String(wipLimit) : ''); }}
              className={clsx(
                'p-1 rounded transition-colors',
                showWipMenu ? 'bg-accent-violet/10 text-accent-violet' : wipLimit !== null ? 'text-text-tertiary' : 'hover:bg-surface-hover text-text-quaternary'
              )}
              title="Limite WIP"
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
            {showWipMenu && (
              <div
                ref={wipMenuRef}
                className="absolute right-0 top-full mt-1 w-44 bg-surface-primary border border-border-primary rounded-lg shadow-lg z-30 p-2"
              >
                <label className="block text-[10px] text-text-tertiary font-medium mb-1">Limite WIP</label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="0"
                    value={wipDraft}
                    onChange={(e) => setWipDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = parseInt(wipDraft);
                        onWipLimitChange(isNaN(val) || val <= 0 ? null : val);
                        setShowWipMenu(false);
                      }
                      if (e.key === 'Escape') setShowWipMenu(false);
                    }}
                    placeholder="Nessun limite"
                    autoFocus
                    className="flex-1 px-2 py-1 text-[11px] bg-surface-tertiary border border-border-primary rounded text-text-primary placeholder:text-text-quaternary focus:border-accent-violet focus:outline-none"
                  />
                  {wipLimit !== null && (
                    <button
                      onClick={() => { onWipLimitChange(null); setShowWipMenu(false); }}
                      className="p-1 rounded hover:bg-surface-hover text-text-quaternary hover:text-red-400 transition-colors"
                      title="Rimuovi limite"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick filter input */}
        {showFilter && (
          <div className="mt-2 relative">
            <input
              ref={filterInputRef}
              type="text"
              value={filterText}
              onChange={(e) => onFilterChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  onFilterChange('');
                  setShowFilter(false);
                }
              }}
              placeholder="Filtra task..."
              className="w-full pl-2.5 pr-7 py-1.5 rounded-md bg-surface-primary border border-border-primary text-[11px] text-text-primary placeholder:text-text-quaternary focus:outline-none focus:border-accent-violet/50 focus:ring-1 focus:ring-accent-violet/30"
            />
            {filterText && (
              <button
                onClick={() => onFilterChange('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-text-quaternary hover:text-text-secondary"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Virtualized Tasks */}
      <div
        ref={parentRef}
        className="flex-1 overflow-y-auto p-2"
        style={{ maxHeight: 'calc(100vh - 200px)' }}
      >
        {filteredTasks.length === 0 ? (
          <div
            className={clsx(
              'flex items-center justify-center h-24 text-[11px] rounded-lg border-2 border-dashed transition-all',
              isDragOver
                ? 'text-accent-violet border-accent-violet/40 bg-accent-violet/5'
                : 'text-text-quaternary border-transparent'
            )}
          >
            {isDragOver ? 'Rilascia qui' : filterText ? 'Nessun risultato' : 'Nessun task'}
          </div>
        ) : (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const task = filteredTasks[virtualRow.index];
              const isDragging = draggedTaskId === task.id;

              return (
                <div
                  // Guard against occasional duplicate task IDs in the dataset
                  key={`${task.id}-${virtualRow.index}`}
                  ref={rowVirtualizer.measureElement}
                  data-index={virtualRow.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                    paddingBottom: '8px',
                  }}
                >
                  <TaskCard
                    task={task}
                    onClick={() => onTaskClick?.(task)}
                    onUpdate={onUpdateTask}
                    availableAssignees={availableAssignees}
                    availableTags={availableTags}
                    isDragging={isDragging}
                    onDragStart={(e) => onDragStart(e, task.id)}
                    onDragEnd={onDragEnd}
                    onContextMenu={onContextMenu}
                    subtaskCount={subtasksCountMap.get(task.id) || 0}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Task Button */}
      <button className="flex items-center justify-center gap-2 m-2 px-3 py-2 rounded-lg text-[11px] font-medium text-text-quaternary hover:text-text-secondary hover:bg-surface-hover border border-dashed border-border-primary hover:border-border-secondary transition-all">
        <Plus className="w-3.5 h-3.5" />
        <span>Aggiungi task</span>
      </button>
    </div>
  );
}

interface TaskCardProps {
  task: Task;
  onClick?: () => void;
  onUpdate?: (taskId: string, updates: Partial<Task>) => void;
  availableAssignees?: string[];
  availableTags?: string[];
  isDragging?: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd?: () => void;
  onContextMenu?: (e: React.MouseEvent, task: Task) => void;
  subtaskCount?: number;
}

function TaskCard({
  task,
  onClick,
  onUpdate,
  availableAssignees = [],
  availableTags = [],
  isDragging,
  onDragStart,
  onDragEnd,
  onContextMenu,
  subtaskCount = 0,
}: TaskCardProps) {
  const overdue = isOverdue(task.dueDate);
  const status = normalizeStatus(task.status);
  const isCompleted = status === TASK_STATUS.COMPLETED || status === TASK_STATUS.CANCELLED;
  const canEdit = !!onUpdate;

  const handleUpdate = (updates: Partial<Task>) => {
    if (onUpdate) {
      onUpdate(task.id, updates);
    }
  };

  const handleAddAssignee = (assignee: string) => {
    const assignees = task.assignees || [];
    if (!assignees.includes(assignee)) {
      handleUpdate({ assignees: [...assignees, assignee] });
    }
  };

  const handleRemoveAssignee = (assignee: string) => {
    const assignees = task.assignees || [];
    handleUpdate({ assignees: assignees.filter((a) => a !== assignee) });
  };

  const handleAddTag = (tag: string) => {
    const tags = task.tags || [];
    if (!tags.includes(tag)) {
      handleUpdate({ tags: [...tags, tag] });
    }
  };

  const handleRemoveTag = (tag: string) => {
    const tags = task.tags || [];
    handleUpdate({ tags: tags.filter((t) => t !== tag) });
  };

  return (
    <div
      onClick={() => onClick?.()}
      onContextMenu={onContextMenu ? (e) => { e.preventDefault(); onContextMenu(e, task); } : undefined}
      className={clsx(
        'p-3 rounded-lg bg-surface-primary border border-border-primary hover:border-border-secondary hover:shadow-lg cursor-pointer transition-all group relative',
        isDragging && 'opacity-50 scale-95'
      )}
    >
      {/* Cover Image */}
      {(() => {
        const coverPath = (task.attachments || []).find(a => {
          const ext = a.split('.').pop()?.toLowerCase() || '';
          return COVER_IMAGE_EXTS.includes(ext);
        });
        return coverPath ? <CoverImage path={coverPath} /> : null;
      })()}

      {/* Drag Handle */}
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing hover:bg-surface-hover transition-opacity"
        title="Trascina per spostare"
      >
        <GripVertical className="w-3.5 h-3.5 text-text-quaternary" />
      </div>
      {/* Header: Status + Priority */}
      <div className="flex items-center justify-between mb-2 pr-6" onClick={(e) => e.stopPropagation()}>
        <StatusSelect
          value={task.status}
          onChange={(status) => handleUpdate({ status })}
          size="sm"
          showLabel={false}
          disabled={!canEdit}
        />
        <PrioritySelect
          value={task.priority ?? null}
          onChange={(priority) => handleUpdate({ priority })}
          size="sm"
          showLabel={false}
          disabled={!canEdit}
        />
      </div>

      {/* Task Name - Inline Editable */}
      <div className="mb-2" onClick={(e) => e.stopPropagation()}>
        {canEdit ? (
          <EditableText
            value={task.name}
            onChange={(name) => handleUpdate({ name })}
            className={clsx(
              'text-[12px] font-medium leading-snug line-clamp-2',
              isCompleted ? 'text-text-tertiary line-through' : 'text-text-primary'
            )}
            inputClassName="text-[12px] font-medium"
            disabled={!canEdit}
          />
        ) : (
          <h4
            className={clsx(
              'text-[12px] font-medium leading-snug line-clamp-2 cursor-pointer hover:text-accent-violet transition-colors',
              isCompleted ? 'text-text-tertiary line-through' : 'text-text-primary'
            )}
          >
            {task.name}
          </h4>
        )}
      </div>

      {/* Tags - Inline Editable */}
      <div className="mb-2.5" onClick={(e) => e.stopPropagation()}>
        <TagsEditor
          tags={task.tags || []}
          availableTags={availableTags}
          onAdd={handleAddTag}
          onRemove={handleRemoveTag}
          size="sm"
          maxDisplay={2}
          disabled={!canEdit}
        />
      </div>

      {/* Custom Fields - Inline Display */}
      <div className="mb-2.5" onClick={(e) => e.stopPropagation()}>
        <CustomFieldsInline
          task={task}
          onUpdate={canEdit ? handleUpdate : undefined}
          maxDisplay={2}
          size="sm"
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-border-primary/50">
        {/* Due Date - Inline Editable */}
        <div onClick={(e) => e.stopPropagation()}>
          <InlineDatePicker
            value={task.dueDate}
            onChange={(dueDate) => handleUpdate({ dueDate })}
            size="sm"
            showTime={false}
            disabled={!canEdit}
            highlight={overdue && !isCompleted}
          />
        </div>

        {/* Right side: indicators + assignees */}
        <div className="flex items-center gap-2">
          {/* Indicators */}
          {subtaskCount > 0 && (
            <div className="flex items-center gap-0.5 text-text-quaternary" title={`${subtaskCount} subtask`}>
              <GitBranch className="w-3 h-3" />
              <span className="text-[10px] tabular-nums font-medium">{subtaskCount}</span>
            </div>
          )}
          {(task.attachments || []).length > 0 && (
            <div className="flex items-center gap-0.5 text-text-quaternary">
              <Paperclip className="w-3 h-3" />
              <span className="text-[10px] tabular-nums font-medium">
                {(task.attachments || []).length}
              </span>
            </div>
          )}

          {/* Assignees - Inline Editable */}
          <div onClick={(e) => e.stopPropagation()}>
            <AssigneesEditor
              assignees={task.assignees || []}
              availableAssignees={availableAssignees}
              onAdd={handleAddAssignee}
              onRemove={handleRemoveAssignee}
              size="sm"
              maxDisplay={2}
              disabled={!canEdit}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
